import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ColumnAccessDraftColumn,
  ColumnAccessDraftState,
} from 'src/feature/ProjectColumnAccessModal/lib/matrix';
import TableService from 'src/shared/api/table/TableService';
import type { ColumnAccessPermissionLevel, ColumnAccessUserItem } from 'src/shared/api/table/types';
import { setError as setGlobalError, setSuccess as setGlobalSuccess } from 'src/shared/lib/utils';
import { Button, Modal } from 'src/shared/ui';
import { Loader } from 'src/shared/ui/Loader';

import {
  buildColumnAccessDraft,
  buildColumnAccessPayload,
  formatColumnAccessUserDisplayName,
  haveColumnAccessChanges,
  resolveAllowedPermissionLevel,
  resolveBasePermissionLevel,
  toggleColumnEditPermission,
  toggleColumnReadPermission,
} from '../lib/matrix';

interface ProjectColumnAccessModalProps {
  onClose: () => void;
  tableId: number;
  formLabel?: string;
  initialColumnName?: string;
  onSaved?: () => void;
}

const permissionLabels: Record<ColumnAccessPermissionLevel, string> = {
  none: 'Нет доступа',
  read: 'Только чтение',
  edit: 'Редактирование',
};

const roleLabels: Record<string, string> = {
  admin: 'Администратор',
  developer: 'Разработчик',
  form_designer: 'Разработчик',
  operator: 'Оператор',
  data_operator: 'Оператор',
  viewer: 'Наблюдатель',
  report_viewer: 'Наблюдатель',
};

const resolveRoleLabel = (role?: string): string => {
  const normalizedRole = role?.trim().toLowerCase() || '';
  return roleLabels[normalizedRole] || normalizedRole || 'Неизвестная роль';
};

const getContextLabel = (draft: ColumnAccessDraftState | null, formLabel?: string): string => {
  const normalizedLabel = formLabel?.trim();
  if (normalizedLabel) {
    return normalizedLabel;
  }

  const tableName = draft?.table_name?.trim();
  if (tableName) {
    return tableName;
  }

  return `Форма ID ${draft?.table_id || '—'}`;
};

const normalizeColumnName = (value?: string): string => (value || '').trim().toLowerCase();

const getColumnPermissionLevel = (
  column: ColumnAccessDraftColumn,
  userId: number,
): ColumnAccessPermissionLevel => {
  return column.permissions.find(permission => permission.user_id === userId)?.level || 'none';
};

const isColumnHighlighted = (columnName: string, highlightedColumnName: string): boolean => {
  return Boolean(highlightedColumnName) && columnName === highlightedColumnName;
};

export const ProjectColumnAccessModal: FC<ProjectColumnAccessModalProps> = ({
  onClose,
  tableId,
  formLabel,
  initialColumnName,
  onSaved,
}) => {
  const [loadedDraft, setLoadedDraft] = useState<ColumnAccessDraftState | null>(null);
  const [draft, setDraft] = useState<ColumnAccessDraftState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [saveErrorMessage, setSaveErrorMessage] = useState('');

  const highlightedColumnName = useMemo(
    () => normalizeColumnName(initialColumnName),
    [initialColumnName],
  );

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    setSaveErrorMessage('');

    try {
      const { data } = await TableService.getColumnAccessPermissions(tableId);
      const nextDraft = buildColumnAccessDraft(data);
      setLoadedDraft(nextDraft);
      setDraft(nextDraft);
    } catch (error) {
      console.error('Не удалось загрузить права доступа к колонкам', error);
      setErrorMessage('Не удалось загрузить права доступа к колонкам');
    } finally {
      setIsLoading(false);
    }
  }, [tableId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const contextLabel = useMemo(() => getContextLabel(draft, formLabel), [draft, formLabel]);

  const visibleUsers = useMemo(() => {
    return (draft?.users || []).filter(
      user =>
        user.form_can_read ||
        user.form_can_edit ||
        user.has_project_access ||
        user.role?.trim().toLowerCase() === 'admin',
    );
  }, [draft?.users]);

  const hasUnsavedChanges = useMemo(
    () => haveColumnAccessChanges(loadedDraft, draft),
    [draft, loadedDraft],
  );

  const handlePermissionChange = (
    columnName: string,
    userId: number,
    level: ColumnAccessPermissionLevel,
  ) => {
    setDraft(currentDraft => {
      if (!currentDraft) {
        return currentDraft;
      }

      return {
        ...currentDraft,
        columns: currentDraft.columns.map(column => {
          if (column.column_name !== columnName) {
            return column;
          }

          return {
            ...column,
            permissions: column.permissions.map(permission =>
              permission.user_id === userId ? { ...permission, level } : permission,
            ),
          };
        }),
      };
    });
    setSaveErrorMessage('');
  };

  const handleReadToggle = (
    columnName: string,
    user: ColumnAccessUserItem,
    currentLevel: ColumnAccessPermissionLevel,
    checked: boolean,
  ) => {
    handlePermissionChange(
      columnName,
      user.id,
      toggleColumnReadPermission(user, currentLevel, checked),
    );
  };

  const handleEditToggle = (
    columnName: string,
    user: ColumnAccessUserItem,
    currentLevel: ColumnAccessPermissionLevel,
    checked: boolean,
  ) => {
    handlePermissionChange(
      columnName,
      user.id,
      toggleColumnEditPermission(user, currentLevel, checked),
    );
  };

  const handleReset = () => {
    setDraft(loadedDraft);
    setSaveErrorMessage('');
  };

  const handleSave = async () => {
    if (!draft) {
      return;
    }

    setIsSaving(true);
    setSaveErrorMessage('');

    try {
      const { data } = await TableService.replaceColumnAccessPermissions(
        tableId,
        buildColumnAccessPayload(draft),
      );
      const nextDraft = buildColumnAccessDraft(data);
      setLoadedDraft(nextDraft);
      setDraft(nextDraft);
      setGlobalSuccess('Права доступа к колонкам сохранены');
      onSaved?.();
    } catch (error: any) {
      console.error('Не удалось сохранить права доступа к колонкам', error);
      const nextMessage =
        error?.response?.data?.error ||
        'Не удалось сохранить права доступа к колонкам. Повторите попытку.';
      setSaveErrorMessage(nextMessage);
      setGlobalError(nextMessage);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <div className="flex w-[92rem] max-w-[96dvw] flex-col">
        <div className="font-gothampro-500 mb-2 text-2xl text-green-100">
          Матрица прав доступа к колонкам
        </div>
        <div className="font-gothampro-400 mb-5 text-sm dark:text-white">{contextLabel}</div>

        {isLoading && (
          <div className="flex h-48 items-center justify-center">
            <Loader classNames="w-10 h-10" />
          </div>
        )}

        {!isLoading && errorMessage && (
          <div className="rounded border border-pink-100 p-4">
            <div className="font-gothampro-400 text-sm text-pink-100">{errorMessage}</div>
            <div className="mt-4 flex gap-2">
              <Button
                size="small"
                classNames="w-32"
                variant="violetOutline"
                onClick={() => void loadData()}
              >
                Повторить
              </Button>
              <Button size="small" classNames="w-32" variant="greyLight" onClick={onClose}>
                Закрыть
              </Button>
            </div>
          </div>
        )}

        {!isLoading && !errorMessage && (
          <>
            <div className="mb-5 rounded border border-grey-1000 p-4">
              <div className="mb-2 text-sm dark:text-white">
                Матрица показывает всех релевантных пользователей и все колонки формы в одном
                сценарии.
              </div>
              <div className="text-xs opacity-70 dark:text-white">
                Права колонки могут только сужать базовый доступ к форме. Чекбокс
                &nbsp;`Редактирование` всегда включает `Чтение`, а ролевые ограничения не дают
                выставить несовместимое состояние.
              </div>
            </div>

            {saveErrorMessage && (
              <div className="mb-4 rounded border border-pink-100 p-3 text-sm text-pink-100">
                {saveErrorMessage}
              </div>
            )}

            {!draft?.columns?.length && (
              <div className="mb-5 rounded border border-grey-1000 p-4 text-sm opacity-70 dark:text-white">
                В форме нет колонок, доступных для настройки.
              </div>
            )}

            {!!draft?.columns?.length && (
              <div className="mb-5 overflow-auto rounded border border-grey-1000">
                <table className="min-w-full border-separate border-spacing-0">
                  <thead className="sticky top-0 z-10 bg-white dark:bg-violet-1200">
                    <tr>
                      <th
                        rowSpan={2}
                        className="border-b border-r border-grey-1000 px-3 py-3 text-left text-extra-xs uppercase opacity-60 dark:text-white"
                      >
                        Пользователь
                      </th>
                      <th
                        rowSpan={2}
                        className="border-b border-r border-grey-1000 px-3 py-3 text-left text-extra-xs uppercase opacity-60 dark:text-white"
                      >
                        Роль
                      </th>
                      <th
                        rowSpan={2}
                        className="border-b border-r border-grey-1000 px-3 py-3 text-left text-extra-xs uppercase opacity-60 dark:text-white"
                      >
                        База формы
                      </th>
                      {(draft?.columns || []).map(column => {
                        const highlighted = isColumnHighlighted(
                          column.column_name,
                          highlightedColumnName,
                        );

                        return (
                          <th
                            key={`column-head-${column.column_name}`}
                            colSpan={2}
                            className={`border-b border-r border-grey-1000 px-3 py-3 text-center text-extra-xs uppercase opacity-60 dark:text-white ${
                              highlighted ? 'bg-blue-rgba-100 dark:bg-violet-1100' : ''
                            }`}
                          >
                            <div className="min-w-32">
                              <div>{column.column_label || column.column_name}</div>
                              <div className="mt-1 normal-case opacity-60">
                                {column.column_type}
                              </div>
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                    <tr>
                      {(draft?.columns || []).map(column => {
                        const highlighted = isColumnHighlighted(
                          column.column_name,
                          highlightedColumnName,
                        );

                        return (
                          <React.Fragment key={`column-subhead-${column.column_name}`}>
                            <th
                              className={`border-b border-r border-grey-1000 px-3 py-2 text-center text-extra-xs uppercase opacity-60 dark:text-white ${
                                highlighted ? 'bg-blue-rgba-100 dark:bg-violet-1100' : ''
                              }`}
                            >
                              Чтение
                            </th>
                            <th
                              className={`border-b border-r border-grey-1000 px-3 py-2 text-center text-extra-xs uppercase opacity-60 dark:text-white ${
                                highlighted ? 'bg-blue-rgba-100 dark:bg-violet-1100' : ''
                              }`}
                            >
                              Ред.
                            </th>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {!visibleUsers.length && (
                      <tr>
                        <td
                          colSpan={3 + (draft?.columns?.length || 0) * 2}
                          className="p-4 text-sm opacity-70 dark:text-white"
                        >
                          Нет пользователей с доступом к форме или проекту.
                        </td>
                      </tr>
                    )}

                    {visibleUsers.map(user => {
                      const baseLevel = resolveBasePermissionLevel(user);
                      const allowedLevel = resolveAllowedPermissionLevel(user);

                      return (
                        <tr key={`column-access-user-${user.id}`}>
                          <td className="border-b border-r border-grey-1000 px-3 py-3 align-top">
                            <div className="min-w-48">
                              <div className="truncate text-sm dark:text-white">
                                {formatColumnAccessUserDisplayName(user.user)}
                              </div>
                              <div className="truncate text-extra-xs opacity-60 dark:text-white">
                                {user.user?.email || `ID ${user.id}`}
                              </div>
                            </div>
                          </td>
                          <td className="border-b border-r border-grey-1000 px-3 py-3 align-top text-xs dark:text-white">
                            {resolveRoleLabel(user.role)}
                          </td>
                          <td className="border-b border-r border-grey-1000 px-3 py-3 align-top text-xs dark:text-white">
                            <div>{permissionLabels[baseLevel]}</div>
                            {allowedLevel !== baseLevel && (
                              <div className="mt-1 opacity-60">
                                Роль ограничивает до: {permissionLabels[allowedLevel]}
                              </div>
                            )}
                          </td>

                          {(draft?.columns || []).map(column => {
                            const currentLevel = getColumnPermissionLevel(column, user.id);
                            const highlighted = isColumnHighlighted(
                              column.column_name,
                              highlightedColumnName,
                            );
                            const readChecked = currentLevel !== 'none';
                            const editChecked = currentLevel === 'edit';
                            const readDisabled = isSaving || allowedLevel === 'none';
                            const editDisabled = isSaving || allowedLevel !== 'edit';
                            const cellClassName = `border-b border-r border-grey-1000 px-3 py-3 align-top text-center ${
                              highlighted ? 'bg-blue-rgba-100 dark:bg-violet-1100' : ''
                            }`;

                            return (
                              <React.Fragment
                                key={`column-access-cell-${column.column_name}-${user.id}`}
                              >
                                <td className={cellClassName}>
                                  <label className="flex justify-center">
                                    <input
                                      aria-label={`Чтение: ${formatColumnAccessUserDisplayName(
                                        user.user,
                                      )} / ${column.column_label || column.column_name}`}
                                      type="checkbox"
                                      checked={readChecked}
                                      disabled={readDisabled}
                                      onChange={event =>
                                        handleReadToggle(
                                          column.column_name,
                                          user,
                                          currentLevel,
                                          event.target.checked,
                                        )
                                      }
                                    />
                                  </label>
                                </td>
                                <td className={cellClassName}>
                                  <label className="flex justify-center">
                                    <input
                                      aria-label={`Редактирование: ${formatColumnAccessUserDisplayName(
                                        user.user,
                                      )} / ${column.column_label || column.column_name}`}
                                      type="checkbox"
                                      checked={editChecked}
                                      disabled={editDisabled}
                                      onChange={event =>
                                        handleEditToggle(
                                          column.column_name,
                                          user,
                                          currentLevel,
                                          event.target.checked,
                                        )
                                      }
                                    />
                                  </label>
                                </td>
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              <Button
                size="small"
                classNames="w-44"
                variant="greyLight"
                onClick={handleReset}
                disabled={isSaving || !hasUnsavedChanges}
              >
                Сбросить черновик
              </Button>
              <Button size="small" classNames="w-28" variant="greyLight" onClick={onClose}>
                Отмена
              </Button>
              <Button
                size="small"
                classNames="w-32"
                variant="violet"
                onClick={() => void handleSave()}
                disabled={isSaving || !hasUnsavedChanges || !draft?.columns?.length}
              >
                {isSaving ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
